import { useState, useEffect, useRef, useMemo, cloneElement } from "react";
import { createPortal } from "react-dom";
import { useStudioErstellen } from "../../../store/studioErstellenStore";
import classes from "../Preis.module.css";
import ErrorMessage from "../ErrorMessage";
import { usePricingCalculations, calculatePayout, calculatePayoutWithWeekend } from "../../../util/usePricingCalculations";

const OutsideSelect = ({ className, children }) => {
    const selectRef = useRef(null);

    const handleClick = (event) => {
        // Only handle clicks on the wrapper, not the native <select> itself.
        if (!selectRef.current || event.target === selectRef.current) return;

        // Prefer showPicker when available (modern Chrome/Edge).
        if (typeof selectRef.current.showPicker === "function") {
            event.preventDefault();
            selectRef.current.showPicker();
            return;
        }

        // Fallback: focus + click (works in many browsers).
        selectRef.current.focus();
        selectRef.current.click();
    };

    return (
        <div
            className={className}
            onClick={handleClick}
        >
            {cloneElement(children, { ref: selectRef })}
        </div>
    );
};

// Helper component for discount item that uses the pricing hook
const DiscountItem = ({ discount, basePrice, classes, onUpdateMinHours, onUpdateDiscount, onRemove, generateHourOptionsForDiscount, generateDiscountOptionsByMode, discountMode }) => {
    const discountObj = discount.discountType && discount.discountValue ? discount : null;
    const payoutCalc = usePricingCalculations(basePrice, discountObj);

    // Guest price after Rabatt.
    const baseNumeric = parseFloat(basePrice) || 0;
    let guestPriceWithDiscount = baseNumeric;
    if (discountObj) {
        const dVal = parseFloat(discountObj.discountValue) || 0;
        if (discountObj.discountType === "percentage") {
            guestPriceWithDiscount = baseNumeric * (1 - dVal / 100);
        } else {
            guestPriceWithDiscount = Math.max(0, baseNumeric - dVal);
        }
    }

    return (
        <div className={classes.discountItem}>
            <div className={classes.discountInputs}>
                <select
                    className="regularMiddle"
                    value={discount.minHours}
                    onChange={(e) => onUpdateMinHours(discount.id, e.target.value)}
                >
                    {generateHourOptionsForDiscount(discount.id, discount.minHours).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>

                <OutsideSelect className={classes.outsideSelect}>
                    <select
                        className="regularMiddle"
                        value={discount.discountValue ? `${discount.discountType}_${discount.discountValue}` : ""}
                        onChange={(e) => onUpdateDiscount(discount.id, e.target.value)}
                    >
                        <option value="">Rabatt wählen</option>
                        {generateDiscountOptionsByMode(discountMode).map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </OutsideSelect>
            </div>
            <div className={classes.discountInfo}>
                <span className="semiboldSmall">
                    {Math.round(guestPriceWithDiscount)}€ / h (Preis für Gäste mit Rabatt)
                </span>
                <span className={`${classes.inputSmallExtra} semiboldXs`}>
                    Dein Payout mit Rabatt: {payoutCalc.payoutRounded}€/h
                </span>
            </div>
            <button
                type="button"
                className={classes.deleteButton}
                onClick={() => onRemove(discount.id)}
            >
                <TrashIcon />
            </button>
        </div>
    );
};

// Helper component for mobile discount payout display
const DiscountPayoutDisplay = ({ basePrice, discount, classes }) => {
    const discountObj = discount && discount.discountType && discount.discountValue ? discount : null;
    const payoutCalc = usePricingCalculations(basePrice, discountObj);

    // Guest price after Rabatt.
    const baseNumeric = parseFloat(basePrice) || 0;
    let guestPriceWithDiscount = baseNumeric;
    if (discountObj) {
        const dVal = parseFloat(discountObj.discountValue) || 0;
        if (discountObj.discountType === "percentage") {
            guestPriceWithDiscount = baseNumeric * (1 - dVal / 100);
        } else {
            guestPriceWithDiscount = Math.max(0, baseNumeric - dVal);
        }
    }

    return (
        <div className={classes.priceGroupPayoutContainerMobile}>
            <span className="semiboldSmall">
                {Math.round(guestPriceWithDiscount)}€ / h (Preis für Gäste mit Rabatt)
            </span>
            <span className={`${classes.inputSmallExtra} semiboldXs`}>
                Dein Payout mit Rabatt: {payoutCalc.payoutRounded}€/h
            </span>
        </div>
    );
};

// (Weekend payout info for surcharge is calculated with a pure helper, not a hook)

const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8.89551 0.100342C9.58831 0.100366 10.1491 0.660607 10.1494 1.35327V2.1687H13.7412C14.4339 2.1687 14.9958 2.72888 14.9961 3.42163V4.57593C14.996 5.09647 14.677 5.53668 14.2266 5.72632L13.5586 14.156L13.541 14.3718L13.5244 14.3552C13.391 14.9129 12.8866 15.2995 12.3145 15.2996H2.80176C2.14224 15.2996 1.59939 14.799 1.54102 14.1443L1.54004 14.1355V14.1316L0.869141 5.71655C0.420447 5.52623 0.103756 5.08723 0.103516 4.56812V3.42163C0.103828 2.72912 0.663915 2.16899 1.35645 2.1687H4.94141V1.35327C4.9417 0.660673 5.50168 0.100509 6.19434 0.100342H8.89551ZM2.59277 14.0564C2.60531 14.1676 2.69378 14.2449 2.80176 14.2449H12.3145C12.4251 14.2448 12.4998 14.1663 12.5059 14.0789L12.5068 14.0691L13.1611 5.82104H1.9375L2.59277 14.0564ZM7.54492 6.97144C7.83603 6.97144 8.07203 7.20773 8.07227 7.49878V12.1238C8.07193 12.4147 7.83602 12.6511 7.54492 12.6511C7.25394 12.651 7.01791 12.4147 7.01758 12.1238V7.49878C7.01781 7.20774 7.25393 6.97153 7.54492 6.97144ZM4.72852 6.98218C4.9506 6.96219 5.15262 7.08331 5.24707 7.27124L5.28125 7.35522L5.28418 7.36597H5.2832L5.2998 7.45093L5.30078 7.45972L5.71973 12.0671L5.7207 12.0769L5.62012 12.0759L5.71973 12.0769V12.1736L5.61914 12.1628L5.71875 12.1736C5.69151 12.4172 5.49654 12.616 5.24316 12.6394C4.9896 12.6625 4.76036 12.5023 4.68945 12.2673L4.68652 12.2576V12.2566L4.6709 12.1726L4.66895 12.1628L4.25098 7.55542L4.25 7.54565H4.25098L4.25195 7.45874V7.44897L4.26953 7.36011C4.32835 7.15816 4.506 7.00241 4.72852 6.98218ZM10.3711 6.98218L10.4756 7.00269C10.7104 7.07356 10.8705 7.30196 10.8477 7.55542L10.4287 12.1628L10.4277 12.1716L10.4121 12.2566L10.4102 12.2673H10.4092C10.3383 12.5024 10.11 12.6624 9.85645 12.6394C9.56652 12.613 9.35282 12.3569 9.37891 12.0671L9.79785 7.45972L9.79883 7.44995L9.81543 7.36499L9.81738 7.35522H9.81836C9.88913 7.12037 10.1172 6.9591 10.3711 6.98218ZM1.35645 3.22339C1.24646 3.22368 1.15851 3.31159 1.1582 3.42163V4.56812C1.15847 4.66023 1.22054 4.73601 1.30566 4.75854H13.8145C13.889 4.72923 13.9414 4.65937 13.9414 4.57593V3.42163C13.9411 3.31163 13.8517 3.22339 13.7412 3.22339H1.35645ZM6.19434 1.15503C6.08425 1.15519 5.99638 1.24311 5.99609 1.35327V2.1687H9.09473V1.35327C9.09444 1.24293 9.00563 1.15505 8.89551 1.15503H6.19434Z" fill="#A1A7AE" stroke="#A1A7AE" strokeWidth="0.2" strokeMiterlimit="10"/>
    </svg>
);

export const Stundenpreis = ({ validate }) => {
    const { studioData, updateStudioData } = useStudioErstellen();

    const [formData, setFormData] = useState({
        minNumberOfHours: studioData.minNumberOfHours || "",
        pricePerHour: studioData.pricePerHour || "",
        adjustPriceByPeople: studioData.adjustPriceByPeople || false,
        priceGroups: studioData.priceGroups || [],
        maxPeople: studioData.capacity || "",
        discounts: studioData.discounts || [],
        fees: studioData.fees || [],
        weekendSurcharge: studioData.weekendSurcharge || false,
        weekendDays: (() => {
            const days = studioData.weekendDays || [];
            // Convert old format (array of strings) to new format (array of objects)
            if (days.length > 0 && typeof days[0] === 'string') {
                return days.map(day => ({ day, percent: "20" }));
            }
            return days;
        })()
    });

    // Ref to track previous store data hash to detect changes
    const prevStoreDataHashRef = useRef(null);

    // Memoize string representations to prevent unnecessary re-renders
    const priceGroupsStr = useMemo(() => JSON.stringify(studioData.priceGroups || []), [studioData.priceGroups]);
    const discountsStr = useMemo(() => JSON.stringify(studioData.discounts || []), [studioData.discounts]);
    const feesStr = useMemo(() => JSON.stringify(studioData.fees || []), [studioData.fees]);
    const weekendDaysStr = useMemo(() => JSON.stringify(studioData.weekendDays || []), [studioData.weekendDays]);

    // Create a comprehensive hash of all store data we care about
    const storeDataHash = useMemo(() => {
        return JSON.stringify({
            minNumberOfHours: studioData.minNumberOfHours,
            pricePerHour: studioData.pricePerHour,
            adjustPriceByPeople: studioData.adjustPriceByPeople,
            priceGroups: priceGroupsStr,
            capacity: studioData.capacity,
            discounts: discountsStr,
            fees: feesStr,
            weekendSurcharge: studioData.weekendSurcharge,
            weekendDays: weekendDaysStr,
            weekendMode: studioData.weekendMode
        });
    }, [
        studioData.minNumberOfHours,
        studioData.pricePerHour,
        studioData.adjustPriceByPeople,
        priceGroupsStr,
        studioData.capacity,
        discountsStr,
        feesStr,
        studioData.weekendSurcharge,
        weekendDaysStr,
        studioData.weekendMode
    ]);

    // Sync formData when studioData changes from store (e.g., after transformation in PreisEditor)
    useEffect(() => {
        // Only update if store data actually changed
        if (prevStoreDataHashRef.current === storeDataHash) {
            return;
        }

        prevStoreDataHashRef.current = storeDataHash;

        const weekendDays = (() => {
            const days = studioData.weekendDays || [];
            // Convert old format (array of strings) to new format (array of objects)
            if (days.length > 0 && typeof days[0] === 'string') {
                return days.map(day => ({ day, percent: "20" }));
            }
            return days;
        })();

        setFormData(prevData => {
            const newData = {
                minNumberOfHours: studioData.minNumberOfHours || "",
                pricePerHour: studioData.pricePerHour || "",
                adjustPriceByPeople: studioData.adjustPriceByPeople || false,
                priceGroups: Array.isArray(studioData.priceGroups) ? studioData.priceGroups : [],
                maxPeople: studioData.capacity || 20,
                discounts: Array.isArray(studioData.discounts) ? studioData.discounts : [],
                fees: Array.isArray(studioData.fees) ? studioData.fees : [],
                weekendSurcharge: studioData.weekendSurcharge || false,
                weekendDays: weekendDays
            };

            // Check if data actually changed using deep comparison
            const hasChanged =
                prevData.minNumberOfHours !== newData.minNumberOfHours ||
                prevData.pricePerHour !== newData.pricePerHour ||
                prevData.adjustPriceByPeople !== newData.adjustPriceByPeople ||
                JSON.stringify(prevData.priceGroups) !== JSON.stringify(newData.priceGroups) ||
                prevData.maxPeople !== newData.maxPeople ||
                JSON.stringify(prevData.discounts) !== JSON.stringify(newData.discounts) ||
                JSON.stringify(prevData.fees) !== JSON.stringify(newData.fees) ||
                prevData.weekendSurcharge !== newData.weekendSurcharge ||
                JSON.stringify(prevData.weekendDays) !== JSON.stringify(newData.weekendDays);

            return hasChanged ? newData : prevData;
        });

        // Sync weekendMode from store
        if (studioData.weekendMode && studioData.weekendMode !== weekendMode) {
            weekendModeUpdateSourceRef.current = 'store';
            prevWeekendModeRef.current = studioData.weekendMode;
            setWeekendMode(studioData.weekendMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeDataHash]);

    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth <= 742 : false
    );

    const [isPriceGroupsModalOpen, setIsPriceGroupsModalOpen] = useState(false);
    const [isDiscountsModalOpen, setIsDiscountsModalOpen] = useState(false);
    const [isFeesModalOpen, setIsFeesModalOpen] = useState(false);
    const [isWeekendModalOpen, setIsWeekendModalOpen] = useState(false);

    // Mobile-only Preisgruppen modal state
    const [priceGroupDraft, setPriceGroupDraft] = useState(null);
    const [editingPriceGroupId, setEditingPriceGroupId] = useState(null);

    // Mobile-only Rabatte modal state
    const [discountDraft, setDiscountDraft] = useState(null);
    const [editingDiscountId, setEditingDiscountId] = useState(null);

    // Global mode toggles for Rabatte & Wochenendaufschlag
    const [discountMode, setDiscountMode] = useState("percentage"); // "percentage" | "amount"
    const [weekendMode, setWeekendMode] = useState(studioData.weekendMode || "percentage");   // "percentage" | "amount"

    // Ref to track if weekendMode update is from user or store
    const weekendModeUpdateSourceRef = useRef('user'); // 'user' | 'store'
    const prevWeekendModeRef = useRef(weekendMode);
    const weekendSectionRef = useRef(null);

    // Ensure discountMode matches existing discounts when loading from store/backend
    useEffect(() => {
        if (formData.discounts && formData.discounts.length > 0) {
            const firstType = formData.discounts[0].discountType;
            if (firstType === "amount" || firstType === "percentage") {
                setDiscountMode(firstType);
            }
        }
    }, [formData.discounts]);

    // Update store when weekendMode changes (only if changed by user)
    useEffect(() => {
        if (weekendModeUpdateSourceRef.current === 'user' && prevWeekendModeRef.current !== weekendMode) {
            updateStudioData('weekendMode', weekendMode);
            prevWeekendModeRef.current = weekendMode;
        }
        weekendModeUpdateSourceRef.current = 'user'; // Reset for next change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekendMode]);

    // Sync weekendMode when store data changes (only if different from current)
    useEffect(() => {
        if (studioData.weekendMode && studioData.weekendMode !== weekendMode) {
            weekendModeUpdateSourceRef.current = 'store';
            prevWeekendModeRef.current = studioData.weekendMode;
            setWeekendMode(studioData.weekendMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studioData.weekendMode]);

    // Mobile-only Gebühren modal state
    const [feeDraft, setFeeDraft] = useState(null);
    const [editingFeeId, setEditingFeeId] = useState(null);

    const [errors, setErrors] = useState({
        pricePerHour: "",
        minNumberOfHours: "",
    });

    const renderMobileSheet = (node) => {
        if (typeof document === "undefined") return null;
        return createPortal(node, document.body);
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 742);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = value.replace(/[^0-9]/g, '');
        setFormData(prevData => ({
            ...prevData,
            [name]: sanitizedValue
        }));
        updateStudioData(name, sanitizedValue);
    };

    const handleKeyDown = (e) => {
        if (['.', ',', '-', '+', 'e', 'E'].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSwitchChange = (field, value) => {
        // Generic switch update
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
        updateStudioData(field, value);

        // SPECIAL CASE: adjustPriceByPeople should enforce EITHER
        // a single hourly price OR grouped prices, never both.
        if (field === "adjustPriceByPeople") {
            if (value) {
                // Switching ON group pricing:
                // Keep the draft value so switching back restores it.
                // The backend payload clears pricePerHour for grouped pricing.
            } else {
                // Switching OFF group pricing:
                // - clear all priceGroups so only single hourly price is used
                setFormData(prevData => ({
                    ...prevData,
                    priceGroups: []
                }));
                updateStudioData("priceGroups", []);
            }
        }
    };

    const scrollToWeekendSectionOnMobile = () => {
        if (!isMobile) return;

        const findScrollableParent = (element) => {
            let parent = element?.parentElement;
            while (parent) {
                const style = window.getComputedStyle(parent);
                const canScrollY = /(auto|scroll)/.test(style.overflowY);
                if (canScrollY && parent.scrollHeight > parent.clientHeight) {
                    return parent;
                }
                parent = parent.parentElement;
            }
            return null;
        };

        const revealWeekendContent = () => {
            if (!weekendSectionRef.current) return;

            const rect = weekendSectionRef.current.getBoundingClientRect();
            const scrollParent = findScrollableParent(weekendSectionRef.current);

            if (scrollParent) {
                const parentRect = scrollParent.getBoundingClientRect();
                const targetScrollTop = scrollParent.scrollTop + (rect.bottom - parentRect.bottom) + 140;
                if (targetScrollTop > scrollParent.scrollTop) {
                    scrollParent.scrollTo({
                        top: targetScrollTop,
                        behavior: "smooth",
                    });
                }
                return;
            }

            const targetScrollTop = window.scrollY + rect.bottom - window.innerHeight + 140;
            if (targetScrollTop > window.scrollY) {
                window.scrollTo({
                    top: targetScrollTop,
                    behavior: "smooth",
                });
            }
        };

        // Run twice to account for late layout growth on mobile.
        setTimeout(revealWeekendContent, 120);
        setTimeout(revealWeekendContent, 320);
    };

    const addPriceGroup = () => {
        if (formData.priceGroups.length >= 5) return;

        const lastGroup = formData.priceGroups[formData.priceGroups.length - 1];
        // Check if last group already reaches max people
        if (lastGroup && parseInt(lastGroup.to) >= formData.maxPeople) return;

        const fromValue = lastGroup ? parseInt(lastGroup.to) + 1 : 1;
        const toValue = Math.min(fromValue + 4, formData.maxPeople);

        const newGroup = {
            id: Date.now(),
            from: fromValue.toString(),
            to: toValue.toString(),
            price: ""
        };

        setFormData(prevData => ({
            ...prevData,
            priceGroups: [...prevData.priceGroups, newGroup]
        }));
        updateStudioData('priceGroups', [...formData.priceGroups, newGroup]);
    };

    const updatePriceGroup = (id, field, value) => {
        let updatedGroups = formData.priceGroups.map(group => {
            if (group.id === id) {
                return { ...group, [field]: value };
            }
            return group;
        });

        // Auto-update "from" of next group if "to" changes
        if (field === 'to') {
            const currentIndex = updatedGroups.findIndex(g => g.id === id);
            if (currentIndex < updatedGroups.length - 1) {
                const nextGroup = updatedGroups[currentIndex + 1];
                const newFrom = parseInt(value) + 1;
                if (newFrom <= formData.maxPeople) {
                    updatedGroups[currentIndex + 1] = {
                        ...nextGroup,
                        from: newFrom.toString()
                    };
                }
            }
        }

        setFormData(prevData => ({
            ...prevData,
            priceGroups: updatedGroups
        }));
        updateStudioData('priceGroups', updatedGroups);
    };

    const removeWeekend = (day) => {
        const filtered = formData.weekendDays.filter(d => d.day !== day);
        setFormData(prevData => ({
            ...prevData,
            weekendDays: filtered
        }));
        updateStudioData('weekendDays', filtered);
        handleCloseWeekendModal();
        if (filtered.length === 0) {
            handleSwitchChange('weekendSurcharge', false);
        }
    };

    const removePriceGroup = (id) => {
        const filtered = formData.priceGroups.filter(g => g.id !== id);
        // Recalculate "from" values
        const recalculated = filtered.map((group, index) => {
            if (index === 0) {
                return { ...group, from: "1" };
            }
            const prevGroup = filtered[index - 1];
            return { ...group, from: (parseInt(prevGroup.to) + 1).toString() };
        });

        setFormData(prevData => ({
            ...prevData,
            priceGroups: recalculated
        }));
        updateStudioData('priceGroups', recalculated);
    };

    const addDiscount = () => {
        const firstAvailable = getFirstAvailableHourForDiscount();
        const newDiscount = {
            id: Date.now(),
            minHours: firstAvailable,
            discountType: "amount",
            discountValue: "",
            basePrice: formData.pricePerHour || "0"
        };

        const updatedDiscounts = [...formData.discounts, newDiscount];
        setFormData(prevData => ({
            ...prevData,
            discounts: updatedDiscounts
        }));
        updateStudioData('discounts', updatedDiscounts);
    };

    const updateDiscount = (id, field, value) => {
        const updated = formData.discounts.map(d =>
            d.id === id ? { ...d, [field]: value } : d
        );

        setFormData(prevData => ({
            ...prevData,
            discounts: updated
        }));
        updateStudioData('discounts', updated);
    };

    const removeDiscount = (id) => {
        const filtered = formData.discounts.filter(d => d.id !== id);
        setFormData(prevData => ({
            ...prevData,
            discounts: filtered
        }));
        updateStudioData('discounts', filtered);
    };

    // ------ Mobile Rabatte helpers ------
    const openDiscountModalForAdd = () => {
        const firstAvailable = getFirstAvailableHourForDiscount();
        setDiscountDraft({
            minHours: firstAvailable,
            discountType: "",
            discountValue: "",
        });
        setEditingDiscountId(null);
        setIsDiscountsModalOpen(true);
    };

    const openDiscountModalForEdit = (discount) => {
        setDiscountDraft({
            minHours: discount.minHours,
            discountType: discount.discountType,
            discountValue: discount.discountValue,
        });
        setEditingDiscountId(discount.id);
        setIsDiscountsModalOpen(true);
    };

    const handleCloseDiscountsModal = () => {
        setIsDiscountsModalOpen(false);
        setDiscountDraft(null);
        setEditingDiscountId(null);
    };

    const handleDiscountSubmit = () => {
        // No draft at all
        if (!discountDraft) {
            handleCloseDiscountsModal();
            return;
        }

        const hasSelection = !!discountDraft.discountType && !!discountDraft.discountValue;
        let updated;

        if (editingDiscountId) {
            if (!hasSelection) {
                // Editing and cleared selection: remove this discount
                updated = formData.discounts.filter(d => d.id !== editingDiscountId);
            } else {
                updated = formData.discounts.map(d => {
                    if (d.id === editingDiscountId) {
                        return {
                            ...d,
                            minHours: discountDraft.minHours,
                            discountType: discountDraft.discountType,
                            discountValue: discountDraft.discountValue,
                        };
                    }
                    return d;
                });
            }
        } else {
            if (!hasSelection) {
                // Adding but nothing chosen → cancel
                handleCloseDiscountsModal();
                return;
            }

            const newDiscount = {
                id: Date.now(),
                minHours: discountDraft.minHours,
                discountType: discountDraft.discountType,
                discountValue: discountDraft.discountValue,
                basePrice: formData.pricePerHour || "0"
            };
            updated = [...formData.discounts, newDiscount];
        }

        setFormData(prevData => ({
            ...prevData,
            discounts: updated
        }));
        updateStudioData('discounts', updated);

        handleCloseDiscountsModal();
    };


    const addFee = () => {
        const newFee = {
            id: Date.now(),
            description: "",
            amount: ""
        };

        const updatedFees = [...formData.fees, newFee];
        setFormData(prevData => ({
            ...prevData,
            fees: updatedFees
        }));
        updateStudioData('fees', updatedFees);
    };

    // ------ Mobile Gebühren helpers ------
    const openFeeModalForAdd = () => {
        setFeeDraft({
            description: "",
            amount: "",
        });
        setEditingFeeId(null);
        setIsFeesModalOpen(true);
    };

    const openFeeModalForEdit = (fee) => {
        setFeeDraft({
            description: fee.description || "",
            amount: fee.amount || "",
        });
        setEditingFeeId(fee.id);
        setIsFeesModalOpen(true);
    };

    const handleCloseFeesModal = () => {
        setIsFeesModalOpen(false);
        setFeeDraft(null);
        setEditingFeeId(null);
    };

    const handleFeeSubmit = () => {
        // No draft at all
        if (!feeDraft) {
            handleCloseFeesModal();
            return;
        }

        const hasContent =
            feeDraft.description.trim() !== "" &&
            String(feeDraft.amount).trim() !== "";
        let updated;

        if (editingFeeId) {
            if (!hasContent) {
                // Editing and cleared both fields: remove this fee completely
                updated = formData.fees.filter(f => f.id !== editingFeeId);
            } else {
                updated = formData.fees.map(f => {
                    if (f.id === editingFeeId) {
                        return {
                            ...f,
                            description: feeDraft.description,
                            amount: feeDraft.amount,
                        };
                    }
                    return f;
                });
            }
        } else {
            if (!hasContent) {
                // Adding but nothing meaningful entered → cancel without creating a fee
                handleCloseFeesModal();
                return;
            }

            const newFee = {
                id: Date.now(),
                description: feeDraft.description,
                amount: feeDraft.amount,
            };
            updated = [...formData.fees, newFee];
        }

        setFormData(prevData => ({
            ...prevData,
            fees: updated
        }));
        updateStudioData('fees', updated);

        handleCloseFeesModal();
    };

    const updateFee = (id, field, value) => {
        const updated = formData.fees.map(f =>
            f.id === id ? { ...f, [field]: value } : f
        );

        setFormData(prevData => ({
            ...prevData,
            fees: updated
        }));
        updateStudioData('fees', updated);
    };

    const removeFee = (id) => {
        const filtered = formData.fees.filter(f => f.id !== id);
        setFormData(prevData => ({
            ...prevData,
            fees: filtered
        }));
        updateStudioData('fees', filtered);
    };

    // ------ Mobile & Desktop Wochenendaufschlag helpers ------
    const handleCloseWeekendModal = () => {
        setIsWeekendModalOpen(false);
        // If no day is selected, do not allow weekend surcharge to stay active
        if (formData.weekendSurcharge && formData.weekendDays.length === 0) {
            handleSwitchChange('weekendSurcharge', false);
        }
    };

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            pricePerHour: "",
            minNumberOfHours: "",
        };

        // Mindeststundenanzahl muss immer ausgefüllt sein (1–12)
        if (formData.minNumberOfHours.toString().trim() === "") {
            newErrors.minNumberOfHours = "Stundenanzahl muss ausgefüllt werden.";
            isValid = false;
        } else if (formData.minNumberOfHours < 1 || formData.minNumberOfHours > 12) {
            newErrors.minNumberOfHours = "Nur zwischen 1 und 12 Stunden.";
            isValid = false;
        }

        // Preis-Logik: Entweder ein Stundenpreis ODER mindestens eine Preisgruppe
        if (!formData.adjustPriceByPeople) {
            const priceValue = formData.pricePerHour.toString().trim();

            if (priceValue === "") {
                newErrors.pricePerHour = "Preis muss ausgefüllt werden.";
                isValid = false;
            } else {
                const numeric = parseFloat(priceValue) || 0;
                if (numeric < 10) {
                    newErrors.pricePerHour = "Preis muss mindestens 10€ betragen.";
                    isValid = false;
                }
                if (numeric > 4000) {
                    newErrors.pricePerHour = "Preis darf maximal 4000€ pro Stunde betragen.";
                    isValid = false;
                }
            }
        } else {
            // Validate price groups (mindestens eine, Preis muss ausgefüllt sein, 10–4000€)
            if (formData.priceGroups.length === 0) {
                newErrors.pricePerHour = "Mindestens eine Preisgruppe muss ausgefüllt werden.";
                isValid = false;
            } else {
                let previousPrice = null;

                formData.priceGroups.forEach((group, index) => {
                    const raw = (group.price || "").toString().trim();
                    if (raw === "") {
                        newErrors.pricePerHour = `Preisgruppe ${index + 1} muss ausgefüllt werden.`;
                        isValid = false;
                    } else {
                        const numeric = parseFloat(raw) || 0;
                        if (numeric < 10) {
                            newErrors.pricePerHour = `Preisgruppe ${index + 1} muss mindestens 10€ betragen.`;
                            isValid = false;
                        }
                        if (numeric > 4000) {
                            newErrors.pricePerHour = `Preisgruppe ${index + 1} darf maximal 4000€ betragen.`;
                            isValid = false;
                        }
                        // Ensure that Preis für höhere Personengruppen nicht niedriger ist
                        if (previousPrice !== null && numeric < previousPrice) {
                            newErrors.pricePerHour = "Der Preis für höhere Personengruppen darf nicht niedriger sein als für kleinere Personengruppen.";
                            isValid = false;
                        }

                        if (!isNaN(numeric)) {
                            previousPrice = numeric;
                        }
                    }

                    // Validate maxPersons doesn't exceed capacity
                    const maxPersons = parseInt(group.to) || 0;
                    if (maxPersons > formData.maxPeople) {
                        newErrors.pricePerHour = `Preisgruppe ${index + 1}: Maximale Personenanzahl (${maxPersons}) darf ${formData.maxPeople} nicht überschreiten.`;
                        isValid = false;
                    }
                });

                // Zusätzlich sicherstellen, dass alle Personen bis zur Kapazität abgedeckt sind
                const capacity = parseInt(formData.maxPeople) || 0;
                if (capacity > 0) {
                    const lastGroup = formData.priceGroups[formData.priceGroups.length - 1];
                    const lastMax = parseInt(lastGroup?.to) || 0;
                    if (lastMax < capacity) {
                        newErrors.pricePerHour = `Lege Preisgruppen lückenlos bis ${capacity} Personen fest.`;
                        isValid = false;
                    }
                }
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, formData]);

    // Initialize first price group if adjustPriceByPeople is enabled (desktop only)
    useEffect(() => {
        if (!isMobile && formData.adjustPriceByPeople && formData.priceGroups.length === 0) {
            const firstGroup = {
                id: Date.now(),
                from: "1",
                to: Math.min(15, formData.maxPeople).toString(),
                price: ""
            };
            setFormData(prevData => ({
                ...prevData,
                priceGroups: [firstGroup]
            }));
            updateStudioData('priceGroups', [firstGroup]);
        }
    }, [formData.adjustPriceByPeople, formData.maxPeople, isMobile]);

    // ------ Mobile Preisgruppen helpers ------
    const openPriceGroupModalForAdd = () => {
        if (formData.priceGroups.length >= 5) return;

        const lastGroup = formData.priceGroups[formData.priceGroups.length - 1];
        // Check if last group already reaches max people
        if (lastGroup && parseInt(lastGroup.to) >= formData.maxPeople) return;

        const fromValue = lastGroup ? parseInt(lastGroup.to) + 1 : 1;
        const toValue = Math.min(fromValue + 14, formData.maxPeople); // e.g. 1 bis 15

        setPriceGroupDraft({
            from: fromValue.toString(),
            to: toValue.toString(),
            price: ""
        });
        setEditingPriceGroupId(null);
        setIsPriceGroupsModalOpen(true);
    };

    const openPriceGroupModalForEdit = (group) => {
        setPriceGroupDraft({
            from: group.from,
            to: group.to,
            price: group.price
        });
        setEditingPriceGroupId(group.id);
        setIsPriceGroupsModalOpen(true);
    };

    const handleClosePriceGroupsModal = () => {
        setIsPriceGroupsModalOpen(false);
        setPriceGroupDraft(null);
        setEditingPriceGroupId(null);

        // If nothing was created, turn the switch off again
        if (formData.priceGroups.length === 0 && formData.adjustPriceByPeople) {
            handleSwitchChange("adjustPriceByPeople", false);
        }
    };

    const handlePriceGroupSubmit = () => {
        if (!priceGroupDraft || !priceGroupDraft.price || !priceGroupDraft.to) {
            // Nothing meaningful entered – behave like closing without saving
            handleClosePriceGroupsModal();
            return;
        }

        let updatedGroups;

        if (editingPriceGroupId) {
            // Edit existing group
            updatedGroups = formData.priceGroups.map((g, index) => {
                if (g.id === editingPriceGroupId) {
                    return { ...g, to: priceGroupDraft.to, price: priceGroupDraft.price };
                }
                return g;
            });
        } else {
            // Add new group
            const lastGroup = formData.priceGroups[formData.priceGroups.length - 1];
            const fromValue = lastGroup ? parseInt(lastGroup.to) + 1 : 1;

            const newGroup = {
                id: Date.now(),
                from: fromValue.toString(),
                to: priceGroupDraft.to,
                price: priceGroupDraft.price
            };

            updatedGroups = [...formData.priceGroups, newGroup];
        }

        // Recalculate "from" values to keep continuity
        updatedGroups = updatedGroups.map((group, index) => {
            if (index === 0) {
                return { ...group, from: "1" };
            }
            const prev = updatedGroups[index - 1];
            return { ...group, from: (parseInt(prev.to) + 1).toString() };
        });

        setFormData(prevData => ({
            ...prevData,
            priceGroups: updatedGroups
        }));
        updateStudioData("priceGroups", updatedGroups);

        setIsPriceGroupsModalOpen(false);
        setPriceGroupDraft(null);
        setEditingPriceGroupId(null);
    };

    const generateHourOptions = () => {
        const options = [];
        for (let i = 1; i <= 12; i++) {
            options.push({ value: i.toString(), label: `Ab ${i} Stunden` });
        }
        return options;
    };

    const generateHourOptionsForDiscount = (excludeDiscountId = null, currentMinHours = null) => {
        const allOptions = generateHourOptions();
        // Get all minHours values that are already used by other discounts (normalize to string for comparison)
        const usedHours = formData.discounts
            .filter(d => d.id !== excludeDiscountId && d.minHours != null && d.minHours !== '')
            .map(d => String(d.minHours));

        // Filter out options that are already used; always include current row's value so the select displays correctly
        return allOptions.filter(opt =>
            !usedHours.includes(opt.value) || (currentMinHours != null && String(currentMinHours) === opt.value)
        );
    };

    const getFirstAvailableHourForDiscount = () => {
        const usedHours = (formData.discounts || [])
            .filter(d => d.minHours != null && d.minHours !== '')
            .map(d => String(d.minHours));
        for (let i = 1; i <= 12; i++) {
            if (!usedHours.includes(String(i))) return String(i);
        }
        return "1";
    };

    const generateDiscountOptions = () => {
        const options = [];
        // Percentage options
        for (let i = 5; i <= 50; i += 5) {
            options.push({ value: `percentage_${i}`, label: `${i}% Rabatt`, type: "percentage", amount: i });
        }
        // Amount options
        for (let i = 10; i <= 100; i += 10) {
            options.push({ value: `amount_${i}`, label: `${i}€ Rabatt`, type: "amount", amount: i });
        }
        return options;
    };

    const generateDiscountOptionsByMode = (mode) => {
        return generateDiscountOptions().filter(opt => opt.type === mode);
    };

    const generateSurchargeOptions = () => {
        const options = [];
        for (let i = 5; i <= 50; i += 5) {
            options.push({ value: i.toString(), label: `${i}% Aufschlag` });
        }
        return options;
    };

    const generateWeekendAmountOptions = () => {
        const options = [];
        for (let i = 5; i <= 100; i += 5) {
            options.push({ value: i.toString(), label: `${i}€ Aufschlag` });
        }
        return options;
    };

    const generatePeopleOptions = (max) => {
        const options = [];
        for (let i = 1; i <= max; i++) {
            options.push({ value: i.toString(), label: i.toString() });
        }
        return options;
    };

    return (
        <div className={classes.inputContainer}>
            <div className={classes.peopleContainer}>
                <h3 className={`${classes.inputTitle} semiboldMiddle`}>Bestimme deine Mindeststundenanzahl</h3>
                <input
                    className={`${classes.peopleContainerInput} slimRegularLargeText`}
                    type="number"
                    placeholder="Stunden (von 1 bis 12)"
                    name="minNumberOfHours"
                    value={formData.minNumberOfHours}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    min="1"
                    max="12"
                    step="1"
                    pattern="[0-9]*"
                />
                {errors.minNumberOfHours && <ErrorMessage>{errors.minNumberOfHours}</ErrorMessage>}
            </div>

            <div className={classes.horizontalLine}></div>

            <div className={classes.inputWrapper}>
                <h3 className={`${classes.inputTitle} semiboldMiddle`}>Bestimme deinen Preis pro Stunde</h3>
                <div className={classes.switchRowInvertedBorder}>
                    <label className={classes.switch}>
                        <input
                            type="checkbox"
                            checked={formData.adjustPriceByPeople}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                handleSwitchChange('adjustPriceByPeople', checked);
                                if (checked && isMobile && formData.priceGroups.length === 0) {
                                    openPriceGroupModalForAdd();
                                }
                            }}
                        />
                        <span className={`${classes.slider} semiboldMiddle`}></span>
                    </label>
                    <span className={`${classes.switchLabel} semiboldMiddle`}>Preis an Personenanzahl anpassen</span>
                </div>

                {!formData.adjustPriceByPeople ? (
                    <div className={classes.sizeContainerLarge}>
                        <input
                            className="slimRegularLarge"
                            type="number"
                            placeholder="Preis in Euro"
                            name="pricePerHour"
                            value={formData.pricePerHour}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            min="10"
                            max="4000"
                            step="1"
                            pattern="[0-9]*"
                        />
                        <div className={classes.inputDescriptionWrapper}>
                            <span className={`${classes.inputDescription} slimRegularLargeText`}>€</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {!isMobile && (
                            <div className={classes.priceGroupsContainer}>
                                <div className={classes.priceGroupHeader}>
                                    <span style={{color: "var(--grey-1)"}} className="semiboldXs">Teilnehmer</span>
                                    <div className={classes.priceGroupPayoutContainer}>
                                        <span style={{color: "var(--grey-1)"}} className="semiboldXs">Payout</span>
                                        <span style={{color: "var(--grey-1)"}} className="semiboldXs">Preis</span>
                                    </div>
                                </div>
                                {formData.priceGroups.map((group, index) => {
                                    const fromValue = index === 0
                                        ? 1
                                        : (parseInt(formData.priceGroups[index - 1].to) || 0) + 1;

                                    return (
                                    <div key={group.id} className={classes.priceGroup}>
                                        <div className={classes.priceGroupInputs}>
                                            <div className={classes.dropdownContainer}>
                                                <span className="slimRegularLargeText">{fromValue}</span>
                                            </div>
                                            <div className={classes.dropdownContainerSecond}>
                                                <span className="semiboldMiddle">bis</span>
                                                <select
                                                    className="slimRegularLargeText"
                                                    value={group.to}
                                                    onChange={(e) => updatePriceGroup(group.id, 'to', e.target.value)}
                                                >
                                                    {generatePeopleOptions(formData.maxPeople)
                                                        // Allow ranges of size 1 (e.g. 3 bis 3 Personen)
                                                        // so the last group can represent exactly one person.
                                                        .filter(opt => parseInt(opt.value) >= fromValue)
                                                        .map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <span style={{whiteSpace: "nowrap"}} className="semiboldMiddle">
                                                {index === 0 ? "Personen (Basispreis)" : "Personen"}
                                            </span>
                                            <span style={{marginLeft: index === 0 ? "16px" : "102px"}} className={`${classes.priceOutput} semiboldXs`}>
                                                {group.price && parseFloat(group.price) >= 10
                                                    ? `${Math.round(calculatePayout(group.price))}€/h`
                                                    : "–"}
                                            </span>
                                            <div className={classes.sizeContainer}>
                                                <input
                                                    className="slimRegularLarge"
                                                    type="number"
                                                    step="1"
                                                    pattern="[0-9]*"
                                                    placeholder="Preis"
                                                    value={group.price}
                                                    onChange={(e) => updatePriceGroup(group.id, 'price', e.target.value.replace(/[^0-9]/g, ''))}
                                                    onKeyDown={handleKeyDown}
                                                    min="10"
                                                    max="4000"
                                                />
                                                <div className={classes.inputDescriptionWrapper}>
                                                    <span className="semiboldXs">€</span>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={classes.deleteButton}
                                            onClick={() => removePriceGroup(group.id)}
                                        >
                                            <TrashIcon />
                                        </button>
                                    </div>
                                )})}
                                {formData.priceGroups.length < 5 &&
                                 (!formData.priceGroups.length || parseInt(formData.priceGroups[formData.priceGroups.length - 1].to) < formData.maxPeople) && (
                                    <button
                                        type="button"
                                        className={`${classes.addButton} semiboldSmall`}
                                        onClick={addPriceGroup}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        Preisgruppe hinzufügen
                                    </button>
                                )}
                            </div>
                        )}

                        {isMobile && (
                            <div className={classes.mobileSummarySection}>
                                <div className={classes.mobileSummaryList}>
                                    {formData.priceGroups.map((group, index) => (
                                        <div key={group.id} className={classes.mobileSummaryCard}>
                                            {index === 0 && (
                                                <span className={`${classes.mobileSummaryTitle} semiboldSmall`}>
                                                    Basispreis
                                                </span>
                                            )}
                                            <div className={classes.mobileSummaryHeaderWithDelete}>
                                                <div
                                                    className={classes.mobileSummaryHeader}
                                                    onClick={() => openPriceGroupModalForEdit(group)}
                                                >
                                                    <span className="slimRegularLargeText">
                                                        {group.from === group.to
                                                            ? `${group.from} Personen`
                                                            : `${group.from} bis ${group.to} Personen`}
                                                    </span>
                                                    <span className="slimRegularLargeText">
                                                        {group.price || "–"} €
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={classes.mobileSummaryDelete}
                                                    onClick={() => removePriceGroup(group.id)}
                                                >
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                className={`${classes.mobileSummaryEdit} semiboldXs`}
                                                onClick={() => openPriceGroupModalForEdit(group)}
                                            >
                                                Preisgruppe bearbeiten
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {formData.priceGroups.length < 5 &&
                                 (!formData.priceGroups.length || parseInt(formData.priceGroups[formData.priceGroups.length - 1].to) < formData.maxPeople) && (
                                    <button
                                        type="button"
                                        className={`${classes.addButton} semiboldSmall`}
                                        onClick={openPriceGroupModalForAdd}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        Preisgruppe hinzufügen
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}

                {!formData.adjustPriceByPeople && formData.pricePerHour && (
                    <span className={`${classes.inputSmallExtra} slimRegularLarge`}>
                        Dein Payout: {Math.round(calculatePayout(formData.pricePerHour))}€/h
                    </span>
                )}
                {errors.pricePerHour && <ErrorMessage style={{marginTop: "12px"}}>{errors.pricePerHour}</ErrorMessage>}
            </div>

            <div className={classes.horizontalLine}></div>

            {/* Erweiterte Preisoptionen */}
            <div className={classes.advancedOptions}>
                <h3 className={`${classes.inputTitle} semiboldMiddle`}>Erweiterte Preisoptionen</h3>

                <div className={classes.advancedOptionsContainer}>
                    {/* Rabatte für längere Buchungen */}
                    <div className={classes.optionSection}>
                        <div
                            className={classes.switchRowInverted}
                            style={{ justifyContent: formData.discounts.length > 0 ? "space-between" : "flex-start" }}
                        >
                            <div className={classes.switchRowInvertedContent}>
                                <label className={classes.switch}>
                                    <input
                                        type="checkbox"
                                        checked={formData.discounts.length > 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                if (isMobile && formData.discounts.length === 0) {
                                                    openDiscountModalForAdd();
                                                } else {
                                                    addDiscount();
                                                }
                                            } else {
                                                setFormData(prevData => ({ ...prevData, discounts: [] }));
                                                updateStudioData('discounts', []);
                                            }
                                        }}
                                    />
                                        <span className={classes.slider}></span>
                                </label>
                                <h4 className="semiboldMiddle">Rabatte für längere Buchungen</h4>
                            </div>
                            {formData.discounts.length > 0 && (
                                <div className={classes.addDiscountImagesContainer}>
                                    <button
                                        type="button"
                                        className={`semiboldMedium ${classes.percentageIcon}`}
                                        style={discountMode === "percentage" ? {} : { opacity: 0.4 }}
                                        onClick={() => setDiscountMode("percentage")}
                                    >
                                        %
                                    </button>
                                    <button
                                        type="button"
                                        className={`semiboldMedium ${classes.percentageIcon}`}
                                        style={discountMode === "amount" ? {} : { opacity: 0.4 }}
                                        onClick={() => setDiscountMode("amount")}
                                    >
                                        €
                                    </button>
                                </div>
                            )}
                        </div>

                        {formData.discounts.length > 0 && !isMobile && (
                            <div className={classes.discountsContainer}>
                                {formData.discounts.map((discount) => {
                                    const basePrice = formData.adjustPriceByPeople && formData.priceGroups.length > 0
                                        ? parseFloat(formData.priceGroups[0].price) || 0
                                        : parseFloat(formData.pricePerHour) || 0;

                                    return (
                                        <DiscountItem
                                            key={discount.id}
                                            discount={discount}
                                            basePrice={basePrice}
                                            classes={classes}
                                            onUpdateMinHours={(id, value) => updateDiscount(id, 'minHours', value)}
                                            onUpdateDiscount={(id, value) => {
                                                if (value) {
                                                    const [type, amount] = value.split('_');
                                                    setFormData(prevData => {
                                                        const updated = prevData.discounts.map(d =>
                                                            d.id === id
                                                                ? { ...d, discountType: type, discountValue: amount }
                                                                : d
                                                        );
                                                        updateStudioData('discounts', updated);
                                                        return {
                                                            ...prevData,
                                                            discounts: updated
                                                        };
                                                    });
                                                } else {
                                                    setFormData(prevData => {
                                                        const updated = prevData.discounts.map(d =>
                                                            d.id === id
                                                                ? { ...d, discountType: "", discountValue: "" }
                                                                : d
                                                        );
                                                        updateStudioData('discounts', updated);
                                                        return {
                                                            ...prevData,
                                                            discounts: updated
                                                        };
                                                    });
                                                }
                                            }}
                                            onRemove={removeDiscount}
                                            generateHourOptionsForDiscount={generateHourOptionsForDiscount}
                                            generateDiscountOptionsByMode={generateDiscountOptionsByMode}
                                            discountMode={discountMode}
                                        />
                                    );
                                })}
                                <button
                                    type="button"
                                    className={`${classes.addButton} semiboldSmall`}
                                    onClick={addDiscount}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    Rabatt hinzufügen
                                </button>
                            </div>
                        )}

                        {formData.discounts.length > 0 && isMobile && (
                            <div className={classes.mobileSummarySection}>
                                <div className={classes.mobileSummaryList}>
                                    {formData.discounts.map((discount) => {
                                        const hourLabel = generateHourOptions().find(
                                            (opt) => opt.value === discount.minHours
                                        )?.label || "";
                                        const discountLabel = discount.discountValue
                                            ? generateDiscountOptions().find(
                                                (opt) =>
                                                    opt.type === discount.discountType &&
                                                    String(opt.amount) === String(discount.discountValue)
                                            )?.label
                                            : "";

                                        return (
                                            <div key={discount.id} className={classes.mobileSummaryCard}>
                                                <div className={classes.mobileSummaryHeaderWithDelete}>
                                                    <div
                                                        className={classes.mobileSummaryHeader}
                                                        onClick={() => openDiscountModalForEdit(discount)}
                                                    >
                                                        <span className="slimRegularLargeText">{hourLabel}</span>
                                                        <span className="slimRegularLargeText">
                                                            {discountLabel || "Rabatt wählen"}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={classes.mobileSummaryDelete}
                                                        onClick={() => removeDiscount(discount.id)}
                                                    >
                                                        <TrashIcon />
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={`${classes.mobileSummaryEdit} semiboldXs`}
                                                    onClick={() => openDiscountModalForEdit(discount)}
                                                >
                                                    Rabatt bearbeiten
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    type="button"
                                    className={`${classes.addButton} semiboldSmall`}
                                    onClick={() => {
                                        openDiscountModalForAdd();
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    Rabatt hinzufügen
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={classes.horizontalLine}></div>

                    {/* Gebühren hinzufügen */}
                    <div className={classes.optionSection} ref={weekendSectionRef}>
                        <div className={classes.switchRowInverted}>
                            <label className={classes.switch}>
                                <input
                                    type="checkbox"
                                    checked={formData.fees.length > 0}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            if (isMobile && formData.fees.length === 0) {
                                                openFeeModalForAdd();
                                            } else {
                                                addFee();
                                            }
                                        } else {
                                            setFormData(prevData => ({ ...prevData, fees: [] }));
                                            updateStudioData('fees', []);
                                        }
                                    }}
                                />
                                <span className={classes.slider}></span>
                            </label>
                            <h4 className="semiboldMiddle">Gebühren hinzufügen</h4>
                        </div>

                        {formData.fees.length > 0 && !isMobile && (
                            <div className={classes.feesContainer}>
                                {formData.fees.map((fee) => (
                                    <div key={fee.id} className={classes.feeItemWrapper}>
                                    <div className={classes.feeItem}>
                                        <input
                                            className="slimRegularLargeText"
                                            type="text"
                                            placeholder="z.B. Reinigungsgebühr"
                                            value={fee.description}
                                            onChange={(e) => updateFee(fee.id, 'description', e.target.value)}
                                        />
                                        <div className={classes.sizeContainer}>
                                            <input
                                                className="slimRegularLarge"
                                                type="number"
                                                step="1"
                                                pattern="[0-9]*"
                                                placeholder="Betrag"
                                                value={fee.amount}
                                                onChange={(e) => updateFee(fee.id, 'amount', e.target.value.replace(/[^0-9]/g, ''))}
                                                onKeyDown={handleKeyDown}
                                                min="10"
                                                max="4000"
                                            />
                                            <div className={classes.inputDescriptionWrapperFee}>
                                                <span className={`${classes.inputDescription} slimRegularLargeText`}>€</span>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className={classes.deleteButton}
                                            onClick={() => removeFee(fee.id)}
                                        >
                                            <TrashIcon />
                                        </button>
                                    </div>
                                    {/* <span className={`${classes.feeTextItem} semiboldXs`}>
                                    Dein Payout: 95€/h
                                    </span> */}
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    className={`${classes.addButton} semiboldSmall`}
                                    onClick={addFee}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    Gebühr hinzufügen
                                </button>
                            </div>
                        )}

                        {formData.fees.length > 0 && isMobile && (
                            <div className={classes.mobileSummarySection}>
                                <div className={classes.mobileSummaryList}>
                                    {formData.fees.map((fee) => (
                                        <div key={fee.id} className={classes.mobileSummaryCard}>
                                            <div
                                                onClick={() => openFeeModalForEdit(fee)}
                                                className={classes.mobileSummaryHeader}
                                            >
                                                <span className="slimRegularLargeText">
                                                    {fee.description || "Gebührenname"}
                                                </span>
                                                <span className="slimRegularLargeText">
                                                    {fee.amount || "–"} €
                                                </span>
                                            </div>
                                            <div className={classes.mobileSummaryEditContainer}>
                                                <button
                                                    type="button"
                                                    className={`${classes.mobileSummaryEdit} semiboldXs`}
                                                    onClick={() => openFeeModalForEdit(fee)}
                                                >
                                                    Gebühr bearbeiten
                                                </button>
                                                <button
                                                    type="button"
                                                    className={classes.mobileSummaryDelete}
                                                    onClick={() => removeFee(fee.id)}
                                                >
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className={`${classes.addButton} semiboldSmall`}
                                    onClick={() => {
                                        openFeeModalForAdd();
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    Gebühr hinzufügen
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={classes.horizontalLine}></div>

                    {/* Wochenendaufschlag */}
                    <div className={classes.optionSection}>
                        <div className={classes.switchRowInverted} style={{ justifyContent: formData.weekendSurcharge ? "space-between" : "flex-start" }}>
                           <div className={classes.switchRowInvertedContent}>
                                <label className={classes.switch}>
                                    <input
                                        type="checkbox"
                                        checked={formData.weekendSurcharge}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            handleSwitchChange('weekendSurcharge', checked);
                                            if (checked && isMobile) {
                                                scrollToWeekendSectionOnMobile();
                                            }
                                            if (checked && isMobile && formData.weekendDays.length === 0) {
                                                setIsWeekendModalOpen(true);
                                            }
                                        }}
                                    />
                                    <span className={classes.slider}></span>
                                </label>
                                <h4 className="semiboldMiddle">Wochenendaufschlag</h4>
                            </div>
                            {formData.weekendSurcharge && (
                                <div className={classes.addDiscountImagesContainer}>
                                    <button
                                        type="button"
                                        className={`semiboldMedium ${classes.percentageIcon}`}
                                        style={weekendMode === "percentage" ? {} : { opacity: 0.4 }}
                                        onClick={() => {
                                            // switch to percentage mode and clear existing surcharge selections
                                            setWeekendMode("percentage");
                                            setFormData(prev => ({
                                                ...prev,
                                                weekendDays: prev.weekendDays.map(d => ({ ...d, percent: "" }))
                                            }));
                                            updateStudioData(
                                                "weekendDays",
                                                formData.weekendDays.map(d => ({ ...d, percent: "" }))
                                            );
                                        }}
                                    >
                                        %
                                    </button>
                                    <button
                                        type="button"
                                        className={`semiboldMedium ${classes.percentageIcon}`}
                                        style={weekendMode === "amount" ? {} : { opacity: 0.4 }}
                                        onClick={() => {
                                            // switch to amount mode and clear existing surcharge selections
                                            setWeekendMode("amount");
                                            setFormData(prev => ({
                                                ...prev,
                                                weekendDays: prev.weekendDays.map(d => ({ ...d, percent: "" }))
                                            }));
                                            updateStudioData(
                                                "weekendDays",
                                                formData.weekendDays.map(d => ({ ...d, percent: "" }))
                                            );
                                        }}
                                    >
                                        €
                                    </button>
                                </div>
                            )}
                        </div>

                        {formData.weekendSurcharge && !isMobile && (
                            <div className={classes.weekendContainer}>
                                {['Samstag', 'Sonntag'].map((day) => {
                                    const daySurcharge = formData.weekendDays.find(d => d.day === day);
                                    const surchargeValue = daySurcharge ? daySurcharge.percent : "20";
                                    const basePrice = formData.adjustPriceByPeople && formData.priceGroups.length > 0
                                        ? parseFloat(formData.priceGroups[0].price) || 0
                                        : parseFloat(formData.pricePerHour) || 0;

                                    return (
                                        <div key={day} className={classes.weekendItem}>
                                            <label className={classes.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!daySurcharge}
                                                    onChange={(e) => {
                                                        let updated;
                                                        if (e.target.checked) {
                                                            updated = [...formData.weekendDays.filter(d => d.day !== day), { day, percent: "20" }];
                                                        } else {
                                                            updated = formData.weekendDays.filter(d => d.day !== day);
                                                        }

                                                        // If no days left, also disable weekend surcharge
                                                        if (updated.length === 0) {
                                                            setFormData(prevData => ({
                                                                ...prevData,
                                                                weekendDays: [],
                                                                weekendSurcharge: false,
                                                            }));
                                                            updateStudioData('weekendDays', []);
                                                            updateStudioData('weekendSurcharge', false);
                                                        } else {
                                                            setFormData(prevData => ({ ...prevData, weekendDays: updated }));
                                                            updateStudioData('weekendDays', updated);
                                                        }
                                                    }}
                                                />
                                                <span className={classes.weekendCheckboxCircle}>
                                                    {daySurcharge && (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <rect width="24" height="24" rx="12" fill="#1F1F1F"/>
                                                            <line x1="6.68079" y1="11.8185" x2="10.2545" y2="15.469" stroke="white" strokeWidth="1.55556"/>
                                                            <line x1="9.16709" y1="15.4506" x2="17.5924" y2="7.43679" stroke="white" strokeWidth="1.55556"/>
                                                        </svg>
                                                    )}
                                                </span>
                                                <span className="semiboldMiddle">{day}</span>
                                            </label>
                                            {daySurcharge && (
                                                <div className={classes.weekendItemContent}>
                                                    <OutsideSelect className={classes.outsideSelect}>
                                                        <select
                                                            className="slimRegularLargeText"
                                                            value={surchargeValue}
                                                            onChange={(e) => {
                                                                const updated = formData.weekendDays.map(d =>
                                                                    d.day === day ? { ...d, percent: e.target.value } : d
                                                                );
                                                                setFormData(prevData => ({ ...prevData, weekendDays: updated }));
                                                                updateStudioData('weekendDays', updated);
                                                            }}
                                                        >
                                                            <option value="">Aufschlag wählen</option>
                                                            {(weekendMode === "percentage"
                                                                ? generateSurchargeOptions()
                                                                : generateWeekendAmountOptions()
                                                            ).map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </select>
                                                    </OutsideSelect>
                                                    {surchargeValue && (() => {
                                                        const weekendCalc = calculatePayoutWithWeekend(
                                                            basePrice,
                                                            { value: surchargeValue },
                                                            weekendMode
                                                        );
                                                        const surchargeNumeric = parseFloat(surchargeValue) || 0;
                                                        const baseNumeric = parseFloat(basePrice) || 0;
                                                        const grossWithSurcharge =
                                                            weekendMode === "percentage"
                                                                ? baseNumeric * (1 + surchargeNumeric / 100)
                                                                : baseNumeric + surchargeNumeric;
                                                        return (
                                                            <div className={classes.weekendItemContentInfo}>
                                                                <span className="semiboldSmall">
                                                                    {Math.round(grossWithSurcharge)}€ / h (Grundpreis mit Aufschlag)
                                                                </span>
                                                                <span className={`${classes.inputSmallExtra} semiboldXs`}>
                                                                    Dein Payout mit Aufschlag: {weekendCalc.payoutRounded}€/h
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {formData.weekendSurcharge && isMobile && (
                            <div className={classes.mobileSummarySection}>
                                <div className={classes.mobileSummaryList}>
                                    {['Samstag', 'Sonntag'].map((day) => {
                                        const daySurcharge = formData.weekendDays.find(d => d.day === day);
                                        if (!daySurcharge) return null;

                                        const value = parseFloat(daySurcharge.percent) || 0;

                                        return (
                                            <div key={day} className={classes.mobileSummaryCard}>
                                                <div
                                                    onClick={() => setIsWeekendModalOpen(true)}
                                                    className={classes.mobileSummaryHeader}
                                                >
                                                    <span className="semiboldMiddle">{day}</span>
                                                    <span className="slimRegularLargeText">
                                                        {weekendMode === "percentage"
                                                            ? `${value || 0} % Aufschlag`
                                                            : `${value || 0} € Aufschlag`}
                                                    </span>
                                                </div>
                                                <div className={classes.mobileSummaryEditContainer}>
                                                    <button
                                                        type="button"
                                                        className={`${classes.mobileSummaryEdit} semiboldXs`}
                                                        onClick={() => setIsWeekendModalOpen(true)}
                                                    >
                                                        Aufschlag bearbeiten
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={classes.mobileSummaryDelete}
                                                        onClick={() => removeWeekend(day)}
                                                    >
                                                        <TrashIcon />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* Mobile bottom sheets */}
            {isMobile && isPriceGroupsModalOpen && renderMobileSheet(
                <div className={classes.mobileSheetOverlay} onClick={handleClosePriceGroupsModal}>
                    <div className={classes.mobileSheet} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.mobileSheetHeader}>
                            <span className="semiboldMiddle">Preisgruppe hinzufügen</span>
                            <button
                                type="button"
                                className={classes.mobileSheetClose}
                                onClick={handleClosePriceGroupsModal}
                            >
                                ×
                            </button>
                        </div>
                        <div className={classes.mobileSheetBody}>
                            {priceGroupDraft && (
                                <>
                                    <div className={classes.priceGroupsContainer}>
                                        <div className={classes.priceGroup}>
                                            <div className={classes.priceGroupInputs}>
                                                <div className={classes.dropdownContainer}>
                                                    <span className="slimRegularLargeText">{priceGroupDraft.from}</span>
                                                </div>
                                                <div className={classes.dropdownContainerSecond}>
                                                    <span className="semiboldMiddle">bis</span>
                                                    <select
                                                        className="slimRegularLargeText"
                                                        value={priceGroupDraft.to}
                                                        onChange={(e) =>
                                                            setPriceGroupDraft(prev => ({
                                                                ...prev,
                                                                to: e.target.value
                                                            }))
                                                        }
                                                    >
                                                        {generatePeopleOptions(formData.maxPeople)
                                                            .filter(opt => parseInt(opt.value) > parseInt(priceGroupDraft.from))
                                                            .map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                    </select>
                                                </div>
                                                <span style={{whiteSpace: "nowrap"}} className="semiboldMiddle">
                                                    Personen (Basispreis)
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={classes.sizeContainerLarge} >
                                            <input
                                                className="slimRegularLarge"
                                                type="number"
                                                step="1"
                                                pattern="[0-9]*"
                                                placeholder="Preis in Euro"
                                                value={priceGroupDraft.price}
                                                onChange={(e) =>
                                                    setPriceGroupDraft(prev => ({
                                                        ...prev,
                                                        price: e.target.value.replace(/[^0-9]/g, "")
                                                    }))
                                                }
                                                onKeyDown={handleKeyDown}
                                            />
                                        <div className={classes.inputDescriptionWrapper}>
                                            <span className={`${classes.inputDescription} slimRegularLargeText`}>€</span>
                                        </div>
                                    </div>

                                    <div className={classes.priceGroupPayoutContainerMobile}>
                                        <span className="semiboldSmall">
                                            {priceGroupDraft.price}€ / h (Basispreis)
                                        </span>
                                        <span className={`${classes.inputSmallExtra} semiboldXs`}>
                                            Dein Payout: {priceGroupDraft.price && parseFloat(priceGroupDraft.price) >= 10
                                                ? `${Math.round(calculatePayout(priceGroupDraft.price))}€/h`
                                                : "–"}
                                        </span>
                                    </div>
                                </>
                            )}

                            <button
                                type="button"
                                className={`${classes.mobileSheetPrimary} mediumLarge`}
                                onClick={handlePriceGroupSubmit}
                            >
                                Preisgruppe hinzufügen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile && isDiscountsModalOpen && renderMobileSheet(
                <div className={classes.mobileSheetOverlay} onClick={handleCloseDiscountsModal}>
                    <div className={classes.mobileSheet} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.mobileSheetHeader}>
                            <span className="semiboldMiddle">Rabatte</span>
                            <button
                                type="button"
                                className={classes.mobileSheetClose}
                                onClick={handleCloseDiscountsModal}
                            >
                                ×
                            </button>
                        </div>
                        <div className={classes.mobileSheetBody}>
                            {discountDraft && (
                                <>
                                    <div className={classes.switchRowInvertedMobile}>
                                        <h4 className="semiboldMiddle">Rabatte für längere Buchungen</h4>
                                        <div className={classes.addDiscountImagesContainerMobile}>
                                            <button
                                                type="button"
                                                className={`semiboldMedium ${classes.percentageIcon}`}
                                                style={discountMode === "percentage" ? {} : { opacity: 0.4 }}
                                                onClick={() => setDiscountMode("percentage")}
                                            >
                                                %
                                            </button>
                                            <button
                                                type="button"
                                                className={`semiboldMedium ${classes.percentageIcon}`}
                                                style={discountMode === "amount" ? {} : { opacity: 0.4 }}
                                                onClick={() => setDiscountMode("amount")}
                                            >
                                                €
                                            </button>
                                        </div>
                                    </div>

                                    <div className={classes.discountInputs}>
                                        <select
                                            className="regularMiddle"
                                            value={discountDraft.minHours}
                                            onChange={(e) => setDiscountDraft(prev => ({
                                                ...prev,
                                                minHours: e.target.value
                                            }))}
                                        >
                                            {generateHourOptionsForDiscount(editingDiscountId, discountDraft.minHours).map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>

                                        <OutsideSelect className={classes.outsideSelect}>
                                            <select
                                                className="regularMiddle"
                                                value={discountDraft.discountType && discountDraft.discountValue ? `${discountDraft.discountType}_${discountDraft.discountValue}` : ""}
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        const [type, amount] = e.target.value.split('_');
                                                        setDiscountDraft(prev => ({
                                                            ...prev,
                                                            discountType: type,
                                                            discountValue: amount
                                                        }));
                                                    } else {
                                                        setDiscountDraft(prev => ({
                                                            ...prev,
                                                            discountType: "",
                                                            discountValue: ""
                                                        }));
                                                    }
                                                }}
                                            >
                                                <option value="">Rabatt wählen</option>
                                            {generateDiscountOptionsByMode(discountMode).map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </OutsideSelect>
                                    </div>

                                    <DiscountPayoutDisplay
                                        basePrice={formData.adjustPriceByPeople && formData.priceGroups.length > 0
                                            ? parseFloat(formData.priceGroups[0].price) || 0
                                            : parseFloat(formData.pricePerHour) || 0}
                                        discount={discountDraft.discountType && discountDraft.discountValue
                                            ? discountDraft
                                            : null}
                                        classes={classes}
                                    />
                                </>
                            )}
                            <button
                                type="button"
                                className={`${classes.mobileSheetPrimary} mediumLarge`}
                                onClick={handleDiscountSubmit}
                            >
                                Rabatt hinzufügen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile && isFeesModalOpen && renderMobileSheet(
                <div className={classes.mobileSheetOverlay} onClick={handleCloseFeesModal}>
                    <div className={classes.mobileSheet} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.mobileSheetHeader}>
                            <span className="semiboldMiddle">
                                {editingFeeId ? "Gebühr bearbeiten" : "Gebühr hinzufügen"}
                            </span>
                            <button
                                type="button"
                                className={classes.mobileSheetClose}
                                onClick={handleCloseFeesModal}
                            >
                                ×
                            </button>
                        </div>
                        <div className={classes.mobileSheetBody}>
                            {feeDraft && (
                                <div className={classes.feesContainer}>
                                    <div className={classes.feeItemWrapper}>
                                        <div className={classes.feeItem}>
                                            <div className={classes.feeItemContent}>
                                                <input
                                                    className="slimRegularLargeText"
                                                    type="text"
                                                    placeholder="z.B. Reinigungsgebühr"
                                                    value={feeDraft.description}
                                                    maxLength={25}
                                                    onChange={(e) =>
                                                        setFeeDraft(prev => ({
                                                            ...prev,
                                                            description: e.target.value
                                                        }))
                                                    }
                                                />
                                                <span className="slimRegularSmallText">
                                                    {feeDraft.description.length}/25
                                                </span>
                                            </div>
                                            <div className={classes.sizeContainer}>
                                                <input
                                                    className="slimRegularLarge"
                                                    type="number"
                                                    step="1"
                                                    pattern="[0-9]*"
                                                    placeholder="Betrag"
                                                    value={feeDraft.amount}
                                                    onChange={(e) =>
                                                        setFeeDraft(prev => ({
                                                            ...prev,
                                                            amount: e.target.value.replace(/[^0-9]/g, '')
                                                        }))
                                                    }
                                                    onKeyDown={handleKeyDown}
                                                min="10"
                                                max="4000"
                                                />
                                                <div className={classes.inputDescriptionWrapperFee}>
                                                    <span className={`${classes.inputDescription} slimRegularLargeText`}>€</span>
                                                </div>
                                            </div>

                                            {editingFeeId && (
                                                <button
                                                    type="button"
                                                    className={classes.deleteButton}
                                                    onClick={() => {
                                                        removeFee(editingFeeId);
                                                        handleCloseFeesModal();
                                                    }}
                                                >
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </div>
                                        {/* <span className={`${classes.feeTextItem} semiboldXs`}>
                                            Dein Payout: 95€/h
                                        </span> */}
                                    </div>
                                </div>
                            )}
                            <button
                                type="button"
                                className={`${classes.mobileSheetPrimary} mediumLarge`}
                                onClick={handleFeeSubmit}
                            >
                                {editingFeeId ? "Gebühr speichern" : "Gebühr hinzufügen"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile && isWeekendModalOpen && renderMobileSheet(
                <div className={classes.mobileSheetOverlay} onClick={handleCloseWeekendModal}>
                    <div className={classes.mobileSheet} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.mobileSheetHeader}>
                            <span className="semiboldMiddle">Wochenendaufschlag</span>
                            <button
                                type="button"
                                className={classes.mobileSheetClose}
                                onClick={handleCloseWeekendModal}
                            >
                                ×
                            </button>
                        </div>
                        <div className={classes.mobileSheetBody}>
                            {formData.weekendSurcharge && (
                                <div className={classes.weekendContainer}>
                                    {['Samstag', 'Sonntag'].map((day) => {
                                        const daySurcharge = formData.weekendDays.find(d => d.day === day);
                                        const surchargePercent = daySurcharge ? daySurcharge.percent : "20";
                                        const basePrice = formData.adjustPriceByPeople && formData.priceGroups.length > 0
                                            ? parseFloat(formData.priceGroups[0].price) || 0
                                            : parseFloat(formData.pricePerHour) || 0;

                                        return (
                                            <div key={day} className={classes.weekendItem}>
                                                <label className={classes.checkboxLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!daySurcharge}
                                                        onChange={(e) => {
                                                            let updated;
                                                            if (e.target.checked) {
                                                                updated = [...formData.weekendDays.filter(d => d.day !== day), { day, percent: "20" }];
                                                            } else {
                                                                updated = formData.weekendDays.filter(d => d.day !== day);
                                                            }
                                                            setFormData(prevData => ({ ...prevData, weekendDays: updated }));
                                                            updateStudioData('weekendDays', updated);
                                                        }}
                                                    />
                                                    <span className={classes.weekendCheckboxCircle}>
                                                        {daySurcharge && (
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                <rect width="24" height="24" rx="12" fill="#1F1F1F"/>
                                                                <line x1="6.68079" y1="11.8185" x2="10.2545" y2="15.469" stroke="white" strokeWidth="1.55556"/>
                                                                <line x1="9.16709" y1="15.4506" x2="17.5924" y2="7.43679" stroke="white" strokeWidth="1.55556"/>
                                                            </svg>
                                                        )}
                                                    </span>
                                                    <span className="semiboldMiddle">{day}</span>
                                                </label>
                                                {daySurcharge && (
                                                    <div className={classes.weekendItemContent}>
                                                        <OutsideSelect className={classes.outsideSelect}>
                                                            <select
                                                                className="slimRegularLargeText"
                                                                value={surchargePercent}
                                                                onChange={(e) => {
                                                                    const updated = formData.weekendDays.map(d =>
                                                                        d.day === day ? { ...d, percent: e.target.value } : d
                                                                    );
                                                                    setFormData(prevData => ({ ...prevData, weekendDays: updated }));
                                                                    updateStudioData('weekendDays', updated);
                                                                }}
                                                            >
                                                                <option value="">Aufschlag wählen</option>
                                                                {generateSurchargeOptions().map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </select>
                                                        </OutsideSelect>
                                                        {surchargePercent && (() => {
                                                            const weekendCalc = calculatePayoutWithWeekend(
                                                                basePrice,
                                                                { value: surchargePercent },
                                                                weekendMode
                                                            );
                                                            const surchargeNumeric = parseFloat(surchargePercent) || 0;
                                                            const baseNumeric = parseFloat(basePrice) || 0;
                                                            const grossWithSurcharge =
                                                                weekendMode === "percentage"
                                                                    ? baseNumeric * (1 + surchargeNumeric / 100)
                                                                    : baseNumeric + surchargeNumeric;
                                                            return (
                                                                <div className={classes.weekendItemContentInfo}>
                                                                    <span className="semiboldSmall">
                                                                        {Math.round(grossWithSurcharge)}€ / h (Grundpreis mit Aufschlag)
                                                                    </span>
                                                                    <span className={`${classes.inputSmallExtra} semiboldXs`}>
                                                                        Dein Payout mit Aufschlag: {weekendCalc.payoutRounded}€/h
                                                                    </span>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <button
                                type="button"
                                className={`${classes.mobileSheetPrimary} mediumLarge`}
                                onClick={() => setIsWeekendModalOpen(false)}
                            >
                                Aufschlag hinzufügen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Stundenpreis;
